import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import {
  checkUserLicenses,
  normalizeEmail,
  UserLicenseStatus
} from "../../../data/queries/userLicense";
import { useAuth } from "../../auth/AuthContext";

const licenseKeys = {
  all: ["userLicenses"] as const,
  byTenant: (tenantId: string) => [...licenseKeys.all, tenantId] as const,
  bulk: (tenantId: string, emails: string[]) =>
    [...licenseKeys.byTenant(tenantId), "bulk", [...emails].sort()] as const
};

export interface UseUserLicensesResult {
  resultByEmail: Record<string, UserLicenseStatus>;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export const useUserLicenses = (emails: string[]): UseUserLicensesResult => {
  const { tenantId } = useAuth();

  const normalizedEmails = useMemo(() => {
    const set = new Set(emails.map(normalizeEmail).filter(Boolean));
    return Array.from(set);
  }, [emails]);

  const query = useQuery({
    queryKey: tenantId
      ? licenseKeys.bulk(tenantId, normalizedEmails)
      : [...licenseKeys.all, "no-tenant"],
    queryFn: async () => {
      if (!tenantId) {
        throw new Error("tenantId required");
      }
      return checkUserLicenses(normalizedEmails, tenantId);
    },
    enabled: Boolean(tenantId) && normalizedEmails.length > 0,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true
  });

  const resultByEmail = useMemo(() => {
    const map: Record<string, UserLicenseStatus> = {};
    (query.data ?? []).forEach((status) => {
      map[normalizeEmail(status.email)] = status;
    });
    return map;
  }, [query.data]);

  return {
    resultByEmail,
    isLoading: query.isLoading,
    error: (query.error as Error | null) ?? null,
    refetch: () => query.refetch()
  };
};
